// Check if a point is within the eraser area
export const isPointInEraserArea = (point, startPoint, endPoint, shape) => {
    if (shape === 'square') {
        // Check if point is within rectangular area
        const minX = Math.min(startPoint.x, endPoint.x);
        const maxX = Math.max(startPoint.x, endPoint.x);
        const minY = Math.min(startPoint.y, endPoint.y);
        const maxY = Math.max(startPoint.y, endPoint.y);
        
        return point.x >= minX && point.x <= maxX && 
               point.y >= minY && point.y <= maxY;
    } else if (shape === 'circle') {
        // Check if point is within circular area
        const centerX = (startPoint.x + endPoint.x) / 2;
        const centerY = (startPoint.y + endPoint.y) / 2;
        const radius = Math.sqrt(
            Math.pow(endPoint.x - startPoint.x, 2) + 
            Math.pow(endPoint.y - startPoint.y, 2)
        ) / 2;
        
        const distance = Math.sqrt(
            Math.pow(point.x - centerX, 2) + 
            Math.pow(point.y - centerY, 2)
        );
        
        return distance <= radius;
    }
    return false;
};

// Split a stroke into segments based on eraser area
export const splitStrokeByEraserArea = (stroke, startPoint, endPoint, shape) => {
    const segments = [];
    let currentSegment = [];
    let wasInEraserArea = false;
    
    stroke.points.forEach((point, index) => {
        const isInEraserArea = isPointInEraserArea(point, startPoint, endPoint, shape);
        
        if (isInEraserArea) {
            // If we're transitioning from outside to inside the eraser area
            if (!wasInEraserArea && currentSegment.length > 0) {
                // Add the current segment to our list
                segments.push([...currentSegment]);
                currentSegment = [];
            }
            wasInEraserArea = true;
        } else {
            // Point is outside eraser area
            if (wasInEraserArea && index > 0) {
                // We're transitioning from inside to outside the eraser area
                // Start a new segment
                currentSegment = [point];
            } else {
                // Continue building the current segment
                currentSegment.push(point);
            }
            wasInEraserArea = false;
        }
    });
    
    // Add the last segment if it exists and has enough points
    if (currentSegment.length > 0) {
        segments.push(currentSegment);
    }
    
    // Filter out segments that are too small to be meaningful
    return segments.filter(segment => segment.length >= 1);
};

// Handle area-based erasing (square or circle) with partial stroke removal
export const handleAreaErase = (strokes, startPoint, endPoint, shape, onStrokeAdded) => {
    const strokesToRemove = [];
    const newStrokesToAdd = [];
    
    strokes.forEach((stroke) => {
        // Check if any part of the stroke is in the eraser area
        const hasPointsInEraserArea = stroke.points.some(point => 
            isPointInEraserArea(point, startPoint, endPoint, shape)
        );
        
        if (hasPointsInEraserArea) {
            // Mark original stroke for removal
            strokesToRemove.push(stroke);
            
            // Split the stroke into segments outside the eraser area
            const segments = splitStrokeByEraserArea(stroke, startPoint, endPoint, shape);
            
            // Create new strokes for each segment that has enough points
            segments.forEach((segmentPoints, segmentIndex) => {
                if (segmentPoints.length >= 1) { // Keep even single points
                    const newStroke = {
                        id: `${stroke.id}_segment_${segmentIndex}_${Date.now()}`,
                        tool: stroke.tool,
                        style: { ...stroke.style },
                        points: segmentPoints,
                        created_at: new Date().toISOString()
                    };
                    newStrokesToAdd.push(newStroke);
                }
            });
        }
    });
    
    // Send batch operation to parent component
    if (strokesToRemove.length > 0 && onStrokeAdded) {
        onStrokeAdded({
            action: 'area-erase',
            strokesToRemove,
            strokesToAdd: newStrokesToAdd
        });
    }
};

// Draw eraser preview overlay
export const drawEraserPreview = (canvasRef, isAreaErasing, eraserStartPoint, eraserEndPoint, tool) => {
    if (!isAreaErasing || !eraserStartPoint || !eraserEndPoint) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    ctx.save();
    
    // Draw the eraser area outline
    ctx.strokeStyle = '#ff0000';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.globalAlpha = 0.8;
    
    if (tool === 'square-eraser') {
        // Draw rectangle preview
        const width = eraserEndPoint.x - eraserStartPoint.x;
        const height = eraserEndPoint.y - eraserStartPoint.y;
        
        ctx.strokeRect(eraserStartPoint.x, eraserStartPoint.y, width, height);
        
        // Fill with semi-transparent red
        ctx.fillStyle = 'rgba(255, 0, 0, 0.15)';
        ctx.fillRect(eraserStartPoint.x, eraserStartPoint.y, width, height);
    } else if (tool === 'circle-eraser') {
        // Draw circle preview
        const centerX = (eraserStartPoint.x + eraserEndPoint.x) / 2;
        const centerY = (eraserStartPoint.y + eraserEndPoint.y) / 2;
        const radius = Math.sqrt(
            Math.pow(eraserEndPoint.x - eraserStartPoint.x, 2) + 
            Math.pow(eraserEndPoint.y - eraserStartPoint.y, 2)
        ) / 2;
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.stroke();
        
        // Fill with semi-transparent red
        ctx.fillStyle = 'rgba(255, 0, 0, 0.15)';
        ctx.fill();
    }
    
    ctx.restore();
};